import { PDFDocument, ParseSpeeds, degrees, type PDFPage } from 'pdf-lib';

export type ProgressFn = (done: number, total: number) => void;

/** pdfjs 模块单例（首次调用 pdfToImages 时加载 + worker 初始化） */
let pdfjsModule: unknown | null = null;
let pdfjsLoading: Promise<unknown> | null = null;

async function loadPdfjs(): Promise<unknown> {
  if (pdfjsModule) return pdfjsModule;
  if (!pdfjsLoading) {
    pdfjsLoading = (async () => {
      try {
        const [mod, urlMod] = await Promise.all([
          import('pdfjs-dist'),
          // 动态 import：Node 下（verify 脚本直接 import 本模块）不会执行到，
          // 避免 Node 解析不了 Vite 的 `?worker&url` 查询语法。
          // 拿到的 URL 指向「启动 Worker」：它在 Worker 全局域先补齐
          // Promise.withResolvers（pdfjs 4.10.38 直接调用该 API，旧内核浏览器会报
          // `Promise.withResolvers is not a function`），再加载真正的 pdfjs worker 模块。
          // 仍走 pdfjs 原生的 workerSrc + ready/test 握手，不自己造轮子。
          import('./pdfjs-worker-url.ts'),
        ]);
        const pdfjs = mod as {
          GlobalWorkerOptions: { workerSrc: string };
        };
        pdfjs.GlobalWorkerOptions.workerSrc = (urlMod as { default: string }).default;
        pdfjsModule = mod;
        return mod;
      } catch (e) {
        pdfjsLoading = null;
        throw new Error(
          `PDF 渲染引擎加载失败（pdf.js 主模块或 worker 未正确打包），请刷新页面。原因：${(
            e as Error
          ).message}`,
        );
      }
    })();
  }
  return pdfjsLoading;
}

export async function mergePdfs(
  files: Array<{ name: string; bytes: Uint8Array }>,
  onProgress?: ProgressFn,
): Promise<Uint8Array> {
  const out = await PDFDocument.create();
  let done = 0;
  for (const f of files) {
    const src = await PDFDocument.load(f.bytes, { ignoreEncryption: true });
    const pages = await out.copyPages(src, src.getPageIndices());
    pages.forEach((p) => out.addPage(p));
    done++;
    onProgress?.(done, files.length);
  }
  return out.save({ useObjectStreams: true });
}

export async function compressPdfs(
  files: Array<{ name: string; bytes: Uint8Array }>,
  onProgress?: ProgressFn,
): Promise<Uint8Array> {
  const out = await PDFDocument.create();
  let done = 0;
  for (const f of files) {
    const src = await PDFDocument.load(f.bytes, { ignoreEncryption: true });
    const pages = await out.copyPages(src, src.getPageIndices());
    pages.forEach((p) => out.addPage(p));
    done++;
    onProgress?.(done, files.length);
  }
  return out.save({
    useObjectStreams: true,
    objectsPerTick: 200,
    // @ts-expect-error - pdf-lib 内部类型未导出，运行时有效
    compress: true,
  });
}

export async function buildPdfFromImages(
  images: Array<{ name: string; bytes: Uint8Array; type?: string }>,
  onProgress?: ProgressFn,
  quality = 0.92,
): Promise<Uint8Array> {
  const doc = await PDFDocument.create();
  let done = 0;
  for (const img of images) {
    let w: ImageBitmap;
    try {
      w = await createImageBitmap(new Blob([img.bytes], { type: img.type }));
    } catch {
      throw new Error(
        `无法解码图片「${img.name}」（可能是 HEIC/AVIF 或损坏的 PNG/JPG）。请转成 JPG 或 PNG 后再试`,
      );
    }
    const page = doc.addPage([w.width, w.height]);
    let embedded;
    if ((img.type === 'image/jpeg' || img.type === 'image/jpg') && quality >= 0.95) {
      // JPEG 且质量几乎无损时直嵌原字节，避免二次重编码损失细节
      embedded = await doc.embedJpg(img.bytes);
    } else {
      const canvas = new OffscreenCanvas(w.width, w.height);
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(w, 0, 0);
      const blob = await canvas.convertToBlob({ type: 'image/jpeg', quality });
      embedded = await doc.embedJpg(new Uint8Array(await blob.arrayBuffer()));
    }
    w.close();
    page.drawImage(embedded, {
      x: 0,
      y: 0,
      width: page.getWidth(),
      height: page.getHeight(),
    });
    done++;
    onProgress?.(done, images.length);
  }
  return doc.save({ useObjectStreams: true });
}

export async function pdfToImages(
  bytes: Uint8Array,
  format: 'image/png' | 'image/jpeg',
  quality: number,
  scale: number,
  onProgress?: (page: number, total: number) => void,
): Promise<Blob[]> {
  const mod = (await loadPdfjs()) as typeof import('pdfjs-dist');
  const pdf = await mod.getDocument({ data: bytes.slice(0) }).promise;
  const blobs: Blob[] = [];
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const viewport = page.getViewport({ scale });
    const canvas = new OffscreenCanvas(viewport.width, viewport.height);
    const ctx = canvas.getContext('2d')!;
    await page.render({ canvasContext: ctx, viewport }).promise;
    const blob = await canvas.convertToBlob({ type: format, quality });
    blobs.push(blob);
    onProgress?.(i, pdf.numPages);
  }
  return blobs;
}

/* ---------- 分割 PDF ---------- */

/** 页码区间，1-based 闭区间（面向用户的页码语义） */
export interface SplitRange {
  start: number;
  end: number;
}

/** 切出的一段（一个独立 PDF） */
export interface SplitSegment {
  /** 文件名，如 'p1-3.pdf' / 'p4.pdf' */
  name: string;
  /** 实际生效的区间（可能因文件页数不足而被裁剪） */
  start: number;
  end: number;
  bytes: Uint8Array;
}

export interface SplitResult {
  segments: SplitSegment[];
  /** 因起始页超出文件页数而被完全跳过的区间数量 */
  skipped: number;
  /** 因结束页超出文件页数而被截短的区间（记录原区间与实际区间） */
  truncated: Array<{ from: SplitRange; to: SplitRange }>;
  /** 源文件总页数 */
  totalPages: number;
}

/**
 * 读取 PDF 页数。
 * 用 ParseSpeeds.Fastest 跳过 xref 修复循环，大文件更快；
 * 加密文档无法正确解析内容，明确抛错而不是返回一个坏结果。
 */
export async function getPageCount(bytes: Uint8Array): Promise<number> {
  const doc = await PDFDocument.load(bytes, {
    ignoreEncryption: true,
    parseSpeed: ParseSpeeds.Fastest,
  });
  if (doc.isEncrypted) {
    throw new Error('该 PDF 已加密，无法分割（请先移除密码保护）');
  }
  return doc.getPageCount();
}

/**
 * 按自定义区间把一个 PDF 切成多个独立 PDF。
 *
 * 源文档只 load 一次，之后每个区间用 copyPages 按索引数组拷页到新文档
 * （copyPages 接受任意索引序列，mergePdfs 的全量拷贝只是它的特例）。
 * 页面 /Rotate 旋转角随页面一并保留；文档级元数据（Title/Author）需手动回填，
 * 因为 copyPages 只搬运页面对象。
 *
 * 区间允许不连续、不重叠。执行期按源文件实际页数裁剪：
 * end 超出页数 → 截断到末页；start 超出页数 → 跳过该区间并计入 skipped。
 */
export async function splitPdfByRanges(
  bytes: Uint8Array,
  ranges: SplitRange[],
  baseName: string,
  onProgress?: (done: number, total: number) => void,
): Promise<SplitResult> {
  const src = await PDFDocument.load(bytes, { ignoreEncryption: true });
  if (src.isEncrypted) {
    throw new Error('该 PDF 已加密，无法分割（请先移除密码保护）');
  }
  const totalPages = src.getPageCount();

  const segments: SplitSegment[] = [];
  const truncated: Array<{ from: SplitRange; to: SplitRange }> = [];
  let skipped = 0;

  for (let i = 0; i < ranges.length; i++) {
    const { start, end } = ranges[i];

    // 整个区间都在文件之外 → 跳过
    if (start > totalPages) {
      skipped++;
      onProgress?.(i + 1, ranges.length);
      continue;
    }

    // 结束页超出 → 截断到末页
    const realEnd = Math.min(end, totalPages);
    if (realEnd < end) truncated.push({ from: { start, end }, to: { start, end: realEnd } });

    const out = await PDFDocument.create();
    const indices: number[] = [];
    for (let p = start; p <= realEnd; p++) indices.push(p - 1);
    const pages = await out.copyPages(src, indices);
    pages.forEach((p) => out.addPage(p));

    // 回填文档级元数据（copyPages 不会搬运）
    out.setTitle(src.getTitle() ?? baseName);
    const author = src.getAuthor();
    if (author) out.setAuthor(author);
    out.setProducer('PDF Toolkit');

    segments.push({
      // 文件名按实际生效的区间命名：被截短时体现截短后的结束页
      name: start === realEnd ? `p${start}.pdf` : `p${start}-${realEnd}.pdf`,
      start,
      end: realEnd,
      bytes: await out.save({ useObjectStreams: true }),
    });
    onProgress?.(i + 1, ranges.length);
  }

  return { segments, skipped, truncated, totalPages };
}

/* ---------- 调整为 A4（虚拟打印式缩放） ---------- */

/** A4 纵向尺寸（PDF 点，1pt = 1/72 英寸）：210mm × 297mm */
export const A4_PT = { width: 595.28, height: 841.89 };

export interface A4ResizeOptions {
  /** 目标方向：auto = 按每页视觉宽高自动选横/纵（打印机行为） */
  orientation: 'auto' | 'portrait' | 'landscape';
  /** fit = 等比缩放居中（不变形）；stretch = 拉伸铺满（宽高比会变） */
  fit: 'fit' | 'stretch';
  /** 页边距（毫米），0 = 无边距 */
  marginMm: number;
}

/** 每页的可见区域 = CropBox ∩ MediaBox（交集退化时退回 MediaBox） */
function visibleBox(page: PDFPage) {
  const media = page.getMediaBox();
  const box = {
    left: media.x,
    bottom: media.y,
    right: media.x + media.width,
    top: media.y + media.height,
  };
  try {
    const crop = page.getCropBox();
    const left = Math.max(box.left, crop.x);
    const bottom = Math.max(box.bottom, crop.y);
    const right = Math.min(box.right, crop.x + crop.width);
    const top = Math.min(box.top, crop.y + crop.height);
    if (right > left && top > bottom) return { left, bottom, right, top };
  } catch {
    /* CropBox 缺失或格式异常：退回 MediaBox */
  }
  return box;
}

/**
 * 把 PDF 每页缩放为标准 A4，类似“虚拟打印到 A4 纸”：
 * 每页作为整体内嵌（Form XObject）到新建的 A4 页上，等比缩放（或拉伸）并居中，
 * 文字保持矢量、可选可搜索。逐页兼容 CropBox 裁剪框、非零原点 MediaBox、
 * /Rotate 90/180/270 旋转页；无内容流的空白页输出空白 A4 占位。
 * 注意：链接、书签、表单填写内容等注释类对象不保留（与打印到纸上的语义一致）。
 */
export async function resizePdfToA4(
  bytes: Uint8Array,
  opts: A4ResizeOptions,
  onProgress?: (done: number, total: number) => void,
): Promise<{ bytes: Uint8Array; pages: number }> {
  const src = await PDFDocument.load(bytes, { ignoreEncryption: true });
  const out = await PDFDocument.create();
  const pages = src.getPages();
  const margin = (opts.marginMm * 72) / 25.4; // mm → pt

  for (let i = 0; i < pages.length; i++) {
    const page = pages[i];
    const box = visibleBox(page);
    const cw = box.right - box.left;
    const ch = box.top - box.bottom;

    // 无内容流的空白页 / 尺寸异常页：输出空白 A4 占位，避免整份文件失败
    if (!page.node.Contents() || !(cw > 0) || !(ch > 0)) {
      const w = opts.orientation === 'landscape' ? A4_PT.height : A4_PT.width;
      const h = opts.orientation === 'landscape' ? A4_PT.width : A4_PT.height;
      out.addPage([w, h]);
      onProgress?.(i + 1, pages.length);
      continue;
    }

    // 页面旋转角（规范要求为 90 的倍数；异常值就近吸附到 90 的倍数并归一化）
    const rot = ((Math.round(page.getRotation().angle / 90) * 90) % 360 + 360) % 360;
    // 90°/270° 旋转页的视觉宽高与页面盒宽高互换
    const [vw, vh] = rot === 90 || rot === 270 ? [ch, cw] : [cw, ch];
    const landscape =
      opts.orientation === 'landscape' || (opts.orientation === 'auto' && vw > vh);
    const aw = landscape ? A4_PT.height : A4_PT.width;
    const ah = landscape ? A4_PT.width : A4_PT.height;
    const availW = Math.max(0, aw - margin * 2);
    const availH = Math.max(0, ah - margin * 2);

    // 内容在 A4 页上的显示尺寸 W×H 与左下角落点 (px, py)
    let W: number;
    let H: number;
    if (opts.fit === 'stretch') {
      W = availW;
      H = availH;
    } else {
      const s = Math.min(availW / vw, availH / vh);
      W = vw * s;
      H = vh * s;
    }
    const px = margin + (availW - W) / 2;
    const py = margin + (availH - H) / 2;

    // 内嵌该页可见区域（显式传 box：兼容非零原点 MediaBox，并按 CropBox 裁剪）
    const embedded = await out.embedPage(page, box);
    const target = out.addPage([aw, ah]);

    // drawPage 的 CTM 为 translate → rotate → scale；源页带 /Rotate 时需换算：
    //   /Rotate 90（显示时顺时针转 90°）→ 绘制转 270°，锚点 (px, py+H)，横纵互换
    //   /Rotate 180 → 绘制转 180°，锚点 (px+W, py+H)
    //   /Rotate 270 → 绘制转 90°，锚点 (px+W, py)
    if (rot === 90 || rot === 270) {
      target.drawPage(embedded, {
        x: rot === 90 ? px : px + W,
        y: rot === 90 ? py + H : py,
        xScale: H / cw,
        yScale: W / ch,
        rotate: degrees((360 - rot) % 360),
      });
    } else {
      target.drawPage(embedded, {
        x: rot === 180 ? px + W : px,
        y: rot === 180 ? py + H : py,
        xScale: W / cw,
        yScale: H / ch,
        rotate: degrees(rot),
      });
    }
    onProgress?.(i + 1, pages.length);
  }

  return { bytes: await out.save({ useObjectStreams: true }), pages: pages.length };
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/**
 * 把 PDF 字节包成 Blob。
 *
 * pdf-lib 的 save() 返回 Uint8Array，在 TS 5.7+ 的 lib 定义里是
 * Uint8Array<ArrayBufferLike>（可能含 SharedArrayBuffer），不能直接当 BlobPart；
 * 运行时它总是普通 ArrayBuffer，这里做一次窄化转换即可。
 */
export function pdfBytesToBlob(bytes: Uint8Array): Blob {
  return new Blob([bytes as unknown as BlobPart], { type: 'application/pdf' });
}

export function formatBytes(n: number) {
  if (!n) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(n) / Math.log(1024));
  return `${(n / 1024 ** i).toFixed(i ? 1 : 0)} ${u[i]}`;
}
